import { useState } from "react";

import { css } from "@compiled/react";

import { Image } from "@/content/templates/template.types.ts";
import { wacToken } from "@/libraries/design/foundations";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

const PlayIcon = () => {
    return (
        <svg
            className="play-icon"
            css={css({
                fill: wacToken("color.neutral.light0"),
                opacity: ".9",
                position: "absolute",
                insetInlineStart: "0",
                insetInlineEnd: "0",
                insetBlockStart: "0",
                insetBlockEnd: "0",
                margin: "auto"
            })}
            width="70px"
            height="70px"
            viewBox="0 0 32 32"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
        >
            <path d="M16,0 C7.163444,-5.41083001e-16 1.082166e-15,7.163444 0,16 C-1.082166e-15,24.836556 7.163444,32 16,32 C24.836556,32 32,24.836556 32,16 C32,11.7565362 30.3142906,7.68687356 27.3137085,4.6862915 C24.3131264,1.68570945 20.2434638,2.5983722e-16 16,0 Z M11.1288889,22.2222222 L11.1288889,9.77777778 L23.6533333,16 L11.1288889,22.2222222 Z" />
        </svg>
    );
};

export type AgileVideoProps = {
    embedLink: string;
    title: string;
    thumbnail: Image | null;
};

export const AgileVideo = (props: AgileVideoProps) => {
    const { embedLink, title, thumbnail } = props;
    const [isPlaying, setIsPlaying] = useState(false);

    const bgImage = `url("${thumbnail?.url}")`;

    const videoOverlayCss = css({
        minHeight: "100%",
        width: "100%",
        position: "relative",
        border: "0",
        cursor: "pointer",
        overflow: "hidden",
        display: "flex"
    });

    const iFrameCss = css({
        verticalAlign: "bottom",
        display: "block",
        aspectRatio: "16/9",
        width: "100%",
        height: "auto",
        allow: "autoplay"
    });

    const videoDataEventProps: DataEventProps = {
        "data-product-key": "jiraSoftware",
        "data-event-component": "linkButton",
        "data-event-container": "videoEmbedInline",
        "data-event": "clicked"
    };

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setIsPlaying(true);
    };

    const VideoSection = () => {
        const isIFrame = embedLink.includes("iframe");
        if (isIFrame) {
            return (
                <div
                    css={css({
                        // NOTE: Nested selectors are necessary here to be able to style the dangerouslySetInnerHTML blob
                        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                        iframe: {
                            verticalAlign: "bottom",
                            display: "block",
                            aspectRatio: "16/9",
                            width: "100%",
                            height: "auto",
                            allow: "autoplay"
                        }
                    })}
                    dangerouslySetInnerHTML={{ __html: embedLink }}
                />
            );
        } else {
            return (
                <iframe
                    css={iFrameCss}
                    width={"100%"}
                    height={"auto"}
                    src={addAutoplay(embedLink)}
                    title={title}
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; autoplay;"
                    allowFullScreen={true}
                    data-gtm-yt-inspected-12="true"
                />
            );
        }

        return null;
    };

    return (
        <div css={css({ overflow: "hidden" })}>
            {!isPlaying && (
                <div css={videoOverlayCss}>
                    <button
                        className="play-trigger"
                        css={css({
                            display: "block",
                            width: "100%",
                            minHeight: "100%"
                        })}
                        onClick={handleClick}
                        {...videoDataEventProps}
                    >
                        {/* eslint-disable-next-line no-restricted-syntax */}
                        <img
                            css={css({
                                "maxWidth": "100%",
                                "width": "100%",
                                "height": "auto",
                                "transformOrigin": "center center",
                                "transform": "scale(1)",
                                "transition": "all 1.3s",
                                "backgroundImage": bgImage,
                                "backgroundSize": "cover",
                                "backgroundPosition": "center center",
                                "backgroundColor": "rgba(23, 43, 77, .15)",
                                "backgroundBlendMode": "multiply",
                                "verticalAlign": "bottom",
                                ":hover": {
                                    backgroundColor: wacToken(
                                        "color.neutral.light0"
                                    ),
                                    transform: "scale(1.06)"
                                }
                            })}
                            src={
                                "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAJCAYAAAA7KqwyAAAAAXNSR0IArs4c6QAAAAlwSFlzAAALEwAACxMBAJqcGAAAAVlpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IlhNUCBDb3JlIDUuNC4wIj4KICAgPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4KICAgICAgPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIKICAgICAgICAgICAgeG1sbnM6dGlmZj0iaHR0cDovL25zLmFkb2JlLmNvbS90aWZmLzEuMC8iPgogICAgICAgICA8dGlmZjpPcmllbnRhdGlvbj4xPC90aWZmOk9yaWVudGF0aW9uPgogICAgICA8L3JkZjpEZXNjcmlwdGlvbj4KICAgPC9yZGY6UkRGPgo8L3g6eG1wbWV0YT4KTMInWQAAABFJREFUKBVjYBgFoyFAhRAAAAJJAAFEkgypAAAAAElFTkSuQmCC"
                            }
                            alt={thumbnail?.name}
                        />
                        <PlayIcon />
                    </button>
                </div>
            )}
            {isPlaying && <VideoSection />}
        </div>
    );
};

const addAutoplay = (url: string) => {
    if (url.includes("?")) {
        return `${url}&autoplay=1`;
    } else {
        return `${url}?autoplay=1`;
    }
};
